<template>
    <h2 class="register-form__title">{{ $t('booking_request') }}</h2>

    <div
        v-if="formError.error"
        v-html="formError.message"
        class="user-exists"
    ></div>

    <Former
        :action="submit"
        :buttonLabel="$t('views.profile.btnSave')"
        :gdprOptions="gdprOptions"
        :agreeOptions="agreeOptions"
        @update:modelValue="formerChange"
    >
        <div class="row">
            <div class="row__col">
                <div class="row">
                    <div class="row__col">
                        <BaseSelect
                            v-model="area"
                            :label="`${ $t('area') }*`"
                            :options="areaOpt"
                            :vMessage="v.getErrors('area')"
                            @blur="validateForm('area')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="row__col">
                        <BaseSelect
                            v-model="how_long"
                            :label="`${ $t('how_long') }?*`"
                            :options="longOpt"
                            :vMessage="v.getErrors('how_long')"
                            @blur="validateForm('how_long')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="row__col">
                        <BaseTextarea
                            v-model="other_specific_area"
                            :label="$t('other_about_area')"
                        />
                    </div>
                </div>
            </div>

            <div class="row__col">
                <div class="row">
                    <div class="row__col">
                        <BaseInput
                            v-model="organization_number"
                            :label="`${ $t('org_nr') }*`"
                            :vMessage="v.getErrors('org_nr')"
                            @blur="validateForm('org_nr')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="row__col">
                        <BaseInput
                            v-model="company_name"
                            :label="`${ $t('company_name') }*`"
                            :vMessage="v.getErrors('company_name')"
                            @blur="validateForm('company_name')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="row__col">
                        <BaseInput
                            v-model="name"
                            :label="`${ $t('name') }*`"
                            :vMessage="v.getErrors('name')"
                            @blur="validateForm('name')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="row__col">
                        <BaseInput
                            v-model="email"
                            :label="`${ $t('email') }*`"
                            :vMessage="v.getErrors('email')"
                            @blur="validateForm('email')"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="row__col">
                        <BaseInput
                            v-model="cell_phone"
                            :label="`${ $t('cellphone') }*`"
                            :vMessage="v.getErrors('cell_phone')"
                            @blur="validateForm('cell_phone')"
                        />
                    </div>
                </div>
            </div>
        </div>
    </Former>
</template>

<script lang="ts">
    import { defineComponent, reactive, ref, toRefs } from 'vue'
    import { useI18n }                                from 'vue-i18n'
    import { companyRegisterEmail }                   from '@services/user/userDataService'
    import { currentLang }                            from '@/helpers'
    import Former                                     from '@components/form/Former.vue'
    import BaseInput                                  from '@components/form/BaseInput.vue'
    import BaseSelect                                 from '@components/form/BaseSelect.vue'
    import BaseTextarea                               from '@components/form/BaseTextarea.vue'
    import suite                                      from '@validations/registerCompany'

    export default defineComponent({
        components: {
            Former,
            BaseInput,
            BaseSelect,
            BaseTextarea
        },
        setup() {
            const { t, locale } = useI18n()

            const v      = ref<any>(suite.get())
            const formError = ref<{
                error: boolean,
                message: string
            }>({
                error: false,
                message: t('form_technical_error')
            })

            const agreed = ref<boolean>(false)
            const gdprOptions = {
                pgf_one: t('external.sublet_register.gdpr_one'),
                pgf_two: t('external.sublet_register.gdpr_two'),
            }
            const agreeOptions = {
                label: t('external.sublet_register.approve'),
                value: false
            }
            const areaOpt = [
                { name: 'Stockholm', value: 'Stockholm', id: 1 },
                { name: 'Göteborg', value: 'Göteborg', id: 2 },
                { name: 'Malmö', value: 'Malmö', id: 3 },
                { name: 'Övriga Sverige', value: 'Övriga Sverige', id: 4 }
            ]
            const estateOpt = [
                { name: '1', value: '1', id: 1 },
                { name: '2-10', value: '2-10', id: 2 },
                { name: '11-20', value: '11-20', id: 3 },
                { name: '21-100', value: '21-100', id: 4 },
            ]

            const longOpt = [
                { name: locale.value === 'en' ? '3-6 months' : '3-6 månader', value: '3-6 månader', id: 1 },
                { name: locale.value === 'en' ? '6-12 months' : '6-12 månader', value: '6-12 månader', id: 2 },
                { name: locale.value === 'en' ? '1-2 years' : '1-2 år', value: '1-2 år', id: 3 }
            ]

            const payload = reactive({
                area: '',
                how_long: '',
                other_specific_area: '',
                organization_number: '',
                company_name: '',
                name: '',
                email: '',
                cell_phone: '',
            })

            const formerChange = (value: { agree: boolean }) => {
                agreed.value = value.agree
            }

            const validateForm = (field?: string) => {
                v.value = suite({
                    area: payload.area,
                    how_long: payload.how_long,
                    org_nr: payload.organization_number,
                    company_name: payload.company_name,
                    name: payload.name,
                    email: payload.email,
                    cell_phone: payload.cell_phone,
                    language: locale
                }, field)

                return v.value.errorCount > 0
            }

            const submit = () => {
                const hasError = validateForm()

                if (!hasError && agreed.value) {

                    sendEmail(payload).then(res => {
                        if (res.status === 'Success') {
                            location.href = `${ location.origin }/${ currentLang }/company-thanks`
                        }

                        if (res.status === 'Error') {
                            const scrollT = document.querySelector('.register-company') as HTMLElement

                            formError.value.error = true

                            window.scrollTo(0, scrollT ? (scrollT.offsetTop - 71) : 0)
                        }
                    })
                } else if (hasError) {
                    setTimeout(() => {
                        document.getElementsByClassName('input-item__error')[0].scrollIntoView();
                    }, 200);
                }
            }

            const sendEmail = async (payload: any) => {
                const result = await companyRegisterEmail(payload).then(response => {
                    if (response.status === 'Success') {
                        return response
                    }
                }).catch(err => {
                    if (err.status === 'Error') {
                        return err
                    }
                })

                return result
            }

            return {
                v,
                formError,
                areaOpt,
                estateOpt,
                longOpt,
                gdprOptions,
                agreeOptions,
                locale,
                ...toRefs(payload),
                formerChange,
                validateForm,
                submit
            }
        }
    })
</script>
